import { Stack } from "@mui/material";
import { type GoalOverview, GoalOverviewHorizon, type QuestionnaireQuestionDto } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useMemo } from "react";
import { useQuestionnaireContext } from "./QuestionnaireContext";
import { QuestionnaireInitBlockHorizon } from "./QuestionnaireInitBlockHorizon";
import { QuestionnaireSectionHeader } from "./QuestionnaireSectionHeader";
import type { RuleState } from "./useRuleEngine";

type Props = {
  questions: QuestionnaireQuestionDto[];
  ruleState: RuleState;
};

const isHorizonVisible = (data: GoalOverview[]): boolean => {
  return data.length > 0;
};

export const QuestionnaireInitBlock = ({ questions, ruleState }: Props) => {
  const { goalsData, investmentPlans } = useQuestionnaireContext();
  const { t } = useResource();

  const shortTermGoals = useMemo(() => goalsData.filter((item) => item.horizon === GoalOverviewHorizon.short), [goalsData]);
  const midTermGoals = useMemo(() => goalsData.filter((item) => item.horizon === GoalOverviewHorizon.middle), [goalsData]);
  const longTermGoals = useMemo(() => goalsData.filter((item) => item.horizon === GoalOverviewHorizon.long), [goalsData]);

  return (
    <Stack spacing={Spacing.XL}>
      <QuestionnaireSectionHeader
        title={t(Resources.Questionnaire.InvestmentGoalsTitle)}
        description={t(Resources.Questionnaire.InvestmentGoalsSubtitle)}
      />
      {isHorizonVisible(shortTermGoals) && (
        <QuestionnaireInitBlockHorizon
          goals={shortTermGoals}
          horizon={GoalOverviewHorizon.short}
          questions={questions}
          investmentPlan={investmentPlans.find((plan) => plan.horizon === GoalOverviewHorizon.short)}
          ruleState={ruleState}
        />
      )}
      {isHorizonVisible(midTermGoals) && (
        <QuestionnaireInitBlockHorizon
          goals={midTermGoals}
          horizon={GoalOverviewHorizon.middle}
          questions={questions}
          investmentPlan={investmentPlans.find((plan) => plan.horizon === GoalOverviewHorizon.middle)}
          ruleState={ruleState}
        />
      )}
      {isHorizonVisible(longTermGoals) && (
        <QuestionnaireInitBlockHorizon
          goals={longTermGoals}
          horizon={GoalOverviewHorizon.long}
          questions={questions}
          investmentPlan={investmentPlans.find((plan) => plan.horizon === GoalOverviewHorizon.long)}
          ruleState={ruleState}
        />
      )}
    </Stack>
  );
};
